import { useState, type ReactNode } from "react";
import { Icon, type IconName } from "../../components/ui/Icon";
import { Info } from "../../components/ui/Info";
import { Orb } from "../../components/ui/Orb";
import { Sheet } from "../../components/ui/Sheet";
import { emailOf, failing, hideProvider, metaOf, shownState, type Connection, type ConnectionKind } from "../../lib/connectionsApi";
import { utcMs } from "../goal/text";
import { useCxFlows } from "./useCxFlows";

/**
 * A product's connections as rows (the prototype's `cxRow`), with their flows: connect, the named checks
 * with their plain reasons, Check again, the ad account and pixel or the mailboxes, and Disconnect. The
 * same rows serve a goal's Get ready and the Connections page. Flashadz runs every check; nothing is
 * ticked by hand.
 */

/** How each kind of connection is shown: the prototype's names, what it is for, and its first button. */
export const CX: Record<ConnectionKind, { icon: IconName; name: string; for: string; btn: string }> = {
  meta: { icon: "bolt", name: "Meta ad account", for: "To launch the Meta tests and read their results", btn: "Connect provider" },
  email: { icon: "mail", name: "Email sending (Smartlead)", for: "To send the cold email tests and read replies", btn: "Connect provider" },
};

export const CX_ORDER: readonly ConnectionKind[] = ["meta", "email"];

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** How long ago, in words: "just now", "12 min ago", "3 h ago", or the day. */
export function when(iso: string | undefined): string {
  const ms = utcMs(iso);
  if (!Number.isFinite(ms)) return "";
  const mins = Math.round((Date.now() - ms) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)} h ago`;
  return new Date(ms).toLocaleDateString("en-US", { day: "numeric", month: "short" });
}

/** When Flashadz last ran a check on the connection. */
export const lastChecked = (c: Connection | undefined) => (c?.checks ?? []).reduce<string | undefined>((m, k) => (!m || k.checkedAt > m ? k.checkedAt : m), undefined);

/** The pill on a row, from the connection's own state (the prototype's `cxPill`). */
export function CxPill({ c }: { c: Connection | undefined }) {
  const st = shownState(c);
  if (st === "ok") return <span className="pill good">Verified · checked {when(lastChecked(c))}</span>;
  if (st === "warming") return <span className="pill live">Warming up</span>;
  if (st === "fix") return <span className="pill tight">{plural(Math.max(1, failing(c?.checks ?? []).length), "thing", "things")} to fix</span>;
  if (st === "checking") return <span className="pill live">Checking</span>;
  return <span className="pill plain">Not connected</span>;
}

interface Props {
  productId: string;
  conns: Connection[] | null;
  kinds: readonly ConnectionKind[];
  reload: () => Promise<void>;
  say: (message: string, done?: boolean) => void;
  /** More under a connected row, such as the mailbox orders under cold email on the Connections page. */
  extra?: Partial<Record<ConnectionKind, ReactNode>>;
}

export function CxRows({ productId, conns, kinds, reload, say, extra }: Props) {
  const f = useCxFlows({ productId, conns, reload, say });
  const [confirmOff, setConfirmOff] = useState<ConnectionKind | null>(null);
  const { sheet, sheetErr, sheetBusy, busy, account, setAccount, pixel, setPixel, key, setKey, mailboxes, chosen, setChosen, byKind, open, close, openAccount, openPixel, fixFor } = f;
  const meta = byKind("meta");

  const disconnect = (k: ConnectionKind) => {
    setConfirmOff(null);
    void f.disconnect(k);
  };

  const row = (k: ConnectionKind) => {
    const c = byKind(k);
    const st = shownState(c);
    const def = CX[k];
    const working = busy === k;
    const m = metaOf(c);
    return (
      <div className="cx" id={`cx-${k}`} key={k}>
        <span className="ki">
          <Icon name={def.icon} />
        </span>
        <div className="cx-b">
          <div className="cx-h">
            <b className="h-i">
              {def.name}
              <Info label="What it is for">{def.for}</Info>
            </b>
            {working ? <span className="pill live">Checking</span> : <CxPill c={c} />}
          </div>
          {st !== "none" && c && (
            <ul className="cks">
              {c.checks.length === 0 && (
                <li>
                  <span className="spin" />
                  <span>Flashadz is checking it now</span>
                </li>
              )}
              {c.checks.map((ck) => {
                const pend = !ck.ok && (ck.optional || (st === "warming" && ck.id === "reputation"));
                const cls = ck.ok ? "ok" : pend ? "pend" : "fail";
                const fix = ck.ok ? null : fixFor(k, ck);
                return (
                  <li key={ck.id} className={cls}>
                    <Icon name={ck.ok ? "check" : pend ? "clock" : "x"} />
                    <span>
                      {ck.label}
                      {ck.ok && ck.detail ? <em> · {hideProvider(ck.detail)}</em> : null}
                    </span>
                    {fix && (
                      <button type="button" className="chip link" onClick={fix}>
                        Fix it
                        <Icon name="right" />
                      </button>
                    )}
                    {!ck.ok && ck.detail && <p className="ck-d">{hideProvider(ck.detail)}</p>}
                  </li>
                );
              })}
            </ul>
          )}
          {k === "meta" && st === "none" && m.lastError && <p className="cx-err">{hideProvider(m.lastError)}</p>}
          {st !== "none" && c && (
            <div className="cx-set">
              {c.secretHint && (
                <span>
                  <b>Key</b> <span className="mono">{c.secretHint}</span>
                </span>
              )}
              {k === "email" && (
                <span>
                  <b>Sending addresses</b> {emailOf(c).mailboxIds.length}
                </span>
              )}
              {f.may && k === "meta" && m.adAccounts.length > 1 && (
                <button type="button" className="chip link" onClick={openAccount}>
                  Change the ad account
                </button>
              )}
              {f.may && k === "meta" && m.signedIn && (
                <button type="button" className="chip link" onClick={openPixel}>
                  {m.pixelId ? "Change the pixel" : "Choose the pixel"}
                </button>
              )}
              {f.may && k === "email" && (
                <button type="button" className="chip link" onClick={() => void f.openMailboxes()}>
                  Change the sending addresses
                </button>
              )}
              {!f.may ? null : confirmOff === k ? (
                <>
                  <span>Disconnect {def.name}?</span>
                  <button type="button" className="wk-undo bad" disabled={working} onClick={() => disconnect(k)}>
                    Disconnect
                  </button>
                  <button type="button" className="wk-undo" onClick={() => setConfirmOff(null)}>
                    Keep it
                  </button>
                </>
              ) : (
                <button type="button" className="wk-undo bad" disabled={working} onClick={() => setConfirmOff(k)}>
                  Disconnect
                </button>
              )}
            </div>
          )}
          {st !== "none" && extra?.[k]}
        </div>
        <div className="cx-a">
          {!f.may ? null : st === "none" ? (
            <button type="button" className="btn sm" onClick={() => f.start(k)}>
              {def.btn}
              {def.btn === "Connect provider" && <Icon name="right" />}
            </button>
          ) : (
            <button type="button" className="btn sm ghost" disabled={working || st === "checking"} onClick={() => void f.checkAgain(k)}>
              Check again
            </button>
          )}
        </div>
      </div>
    );
  };

  const metaAccounts = metaOf(meta).adAccounts;
  const pixels = metaOf(meta).pixels;

  return (
    <>
      {conns ? kinds.map(row) : <p className="load-note">Loading the connections…</p>}
      <Sheet open={sheet !== null} onClose={close} label={sheet === "meta-account" ? "Choose an ad account" : sheet?.startsWith("meta") ? "Connect Meta" : "Connect Smartlead"}>
        {sheet === "meta" && (
          <>
            <div className="eyebrow">Connect</div>
            <h2 style={{ marginTop: 6 }}>Your Meta ad account</h2>
            <p className="lede" style={{ fontSize: "var(--fs-text)" }}>
              Flashadz asks for two things: to manage ads and to read their results. It never posts to your page, and nothing launches without your OK.
            </p>
            {sheetErr && <p className="cx-err">{sheetErr}</p>}
            <button type="button" className="btn wide" disabled={sheetBusy} onClick={() => void f.startMeta()}>
              {sheetBusy ? "Opening…" : "Continue with Facebook"}
            </button>
          </>
        )}
        {sheet === "meta-wait" && (
          <div className="busy">
            <div className="orb-tile">
              <Orb live />
            </div>
            <h3>Waiting for Facebook</h3>
            <p className="fine">Sign in and allow access in the Facebook window.</p>
            {f.popupBlocked && f.hasAuthUrl && (
              <button type="button" className="btn sm" style={{ marginTop: 14 }} onClick={f.reopenPopup}>
                Open the Facebook window
              </button>
            )}
          </div>
        )}
        {sheet === "meta-account" && (
          <>
            <div className="eyebrow">Connect</div>
            <h2 style={{ marginTop: 6 }}>Which ad account?</h2>
            <div className="rows" style={{ marginTop: 12 }}>
              {metaAccounts.map((a) => (
                <label key={a.id} className={`ask-l${a.usable ? "" : " off"}`}>
                  <input type="radio" name="aa" checked={account === a.id} disabled={!a.usable} onChange={() => setAccount(a.id)} />
                  <span className="ask-t">
                    <b>{a.name ?? a.id}</b>
                    <br />
                    <span>{[a.id, a.currency, a.timezone].filter(Boolean).join(" · ")}</span>
                    {!a.usable && a.why && (
                      <>
                        <br />
                        <span>{a.why}</span>
                      </>
                    )}
                  </span>
                </label>
              ))}
            </div>
            {sheetErr && <p className="cx-err">{sheetErr}</p>}
            <button type="button" className="btn wide" disabled={sheetBusy || !account} onClick={() => void f.saveAccount()}>
              {sheetBusy ? "Checking…" : "Use this account"}
            </button>
          </>
        )}
        {sheet === "meta-pixel" && (
          <>
            <div className="eyebrow">Connect</div>
            <h2 style={{ marginTop: 6 }}>Which pixel?</h2>
            <p className="lede" style={{ fontSize: "var(--fs-text)" }}>
              The pixel your pages report to.{pixels.length ? "" : " Meta listed none for this ad account, so paste its ID from Meta Events Manager, under Data sources."}
            </p>
            {pixels.length ? (
              <div className="rows" style={{ marginTop: 12 }}>
                {pixels.map((px) => (
                  <label key={px.id} className="ask-l">
                    <input type="radio" name="px" checked={pixel === px.id} onChange={() => setPixel(px.id)} />
                    <span className="ask-t">
                      <b>{px.name ?? px.id}</b>
                      <br />
                      <span>{[px.id, px.status === "inactive" ? "Meta says it is not active" : null].filter(Boolean).join(" · ")}</span>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <>
                <label className="fl" htmlFor="cx-pixel">
                  Pixel ID
                </label>
                <input className="field" id="cx-pixel" value={pixel} onChange={(e) => setPixel(e.target.value)} inputMode="numeric" autoComplete="off" />
              </>
            )}
            {sheetErr && <p className="cx-err">{sheetErr}</p>}
            <button type="button" className="btn wide" disabled={sheetBusy || !pixel.trim()} onClick={() => void f.savePixel()}>
              {sheetBusy ? "Checking…" : "Use this pixel"}
            </button>
          </>
        )}
        {sheet === "email-key" && (
          <>
            <div className="eyebrow">Connect</div>
            <h2 style={{ marginTop: 6 }}>Smartlead</h2>
            <p className="lede" style={{ fontSize: "var(--fs-text)" }}>
              Paste your Smartlead API key. You find it in Smartlead under Settings, API.
            </p>
            <label className="fl" htmlFor="cx-slk">
              API key
            </label>
            <input className="field" id="cx-slk" type="password" value={key} onChange={(e) => setKey(e.target.value)} autoComplete="off" />
            {sheetErr && <p className="cx-err">{sheetErr}</p>}
            <button type="button" className="btn wide" disabled={sheetBusy || !key.trim()} onClick={() => void f.connectEmail()}>
              {sheetBusy ? "Checking…" : "Connect"}
            </button>
          </>
        )}
        {sheet === "email-mailboxes" && !mailboxes && (
          <>
            <div className="eyebrow">Connect</div>
            <h2 style={{ marginTop: 6 }}>Choose the sending addresses</h2>
            {sheetErr ? (
              <>
                <p className="cx-err">{sheetErr}</p>
                <button type="button" className="btn wide" onClick={() => open("email-key")}>
                  Paste the key again
                </button>
              </>
            ) : (
              <div className="cx-load">
                <span className="spin" />
                <span>Reading your Smartlead addresses</span>
              </div>
            )}
          </>
        )}
        {sheet === "email-mailboxes" && mailboxes && (
          <>
            <div className="eyebrow">Connect</div>
            <h2 style={{ marginTop: 6 }}>Choose the sending addresses</h2>
            <p className="lede" style={{ fontSize: "var(--fs-text)" }}>
              The addresses to send from. Use a separate domain, so cold email never affects your main one.
            </p>
            {mailboxes.length === 0 ? (
              <p className="cx-err">Your Smartlead account has no sending address yet. Add one in Smartlead, then check again.</p>
            ) : (
              <div className="rows" style={{ marginTop: 12 }}>
                {mailboxes.map((mb) => (
                  <label key={mb.id} className="ask-l">
                    <input
                      type="checkbox"
                      checked={chosen.includes(mb.id)}
                      onChange={(e) => setChosen((xs) => (e.target.checked ? [...xs, mb.id] : xs.filter((x) => x !== mb.id)))}
                    />
                    <span className="ask-t">
                      <b>{mb.fromEmail}</b>
                      <br />
                      <span>
                        {[
                          mb.fromName,
                          mb.smtpOk && mb.imapOk ? "sends and reads replies" : "needs reconnecting in Smartlead",
                          mb.maxPerDay !== null ? `up to ${mb.maxPerDay} a day` : "no daily limit set",
                          mb.warmup.reputation !== null ? `trust score ${mb.warmup.reputation}%` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}
            {sheetErr && <p className="cx-err">{sheetErr}</p>}
            <button
              type="button"
              className="btn wide"
              disabled={sheetBusy || chosen.length === 0}
              onClick={() => void f.saveMailboxes()}
            >
              {sheetBusy ? "Checking…" : "Use these addresses"}
            </button>
          </>
        )}
      </Sheet>
    </>
  );
}
